import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {faPen, faToggleOff, faToggleOn, faTrash, faWallet} from '@fortawesome/free-solid-svg-icons';
import {
    ISortCriteriaDto,
    PageableRequestDtoOfCustomerCriteriaDto,
    PagedResultDtoOfCustomerLightDto, CustomRoleDto, CustomRoleService,
    SortCriteriaDto,
    SortDirection,
    CustomerCriteriaDto,
    CustomerSearchCriteriaDto,
    CustomerService,
    SiteDto,
    SiteService
} from '../../_services/configuration-services';
import {PermissionKeys} from '../../app.constants';
import {FormUtils} from '../../_shared/form-utils';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CustomerExportModalComponent} from './customer-export-modal/customer-export-modal.component';
import {Subscription} from 'rxjs';
import {TranslateUtils} from '../../_shared/translate-utils';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {RolesService} from '../../_shared/roles-service';
import {NotificationsService} from '../../_shared/notifications.service';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {IDropdownSettings} from 'ng-multiselect-dropdown';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

@Component({
    selector: 'app-customers',
    templateUrl: './customers.component.html',
    styleUrls: ['./customers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomersComponent implements OnInit, OnDestroy {

    icons = {
        edit: faPen,
        wallet: faWallet,
        trash: faTrash,
        toggleOn: faToggleOn,
        toggleOff: faToggleOff
    }

    customers: PagedResultDtoOfCustomerLightDto = new PagedResultDtoOfCustomerLightDto();

    PermissionKeys = PermissionKeys;

    textInputPattern = FormUtils.textInputPattern;
    crtPage = 0;

    readonly pageSize = 50;
    readonly defaultSortProperty = 'customerNumber';

    private searchTermsSubscription: Subscription;
    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    private pageRequest: PageableRequestDtoOfCustomerCriteriaDto;

    searchTermsFormGroup = new UntypedFormGroup({
        searchTerms: new UntypedFormControl(null)
    });

    filterFormGroup = new UntypedFormGroup({
        customRoleKeys: new UntypedFormControl(null),
        accountEnabled: new UntypedFormControl(null),
        siteCodes: new UntypedFormControl(null)
    });

    multiSelectSettings: IDropdownSettings = {};
    customRoleMultiSelectSource = [];
    roles: CustomRoleDto[] = [];
    siteMultiSelectSource = [];
    sites: SiteDto[] = [];

    constructor(private readonly customerService: CustomerService,
                private readonly modalService: NgbModal,
                private readonly translateService: TranslateService,
                private readonly notificationsService: NotificationsService,
                private readonly customRoleService: CustomRoleService,
                private readonly siteService: SiteService,
                readonly rolesService: RolesService) {

        this.pageRequest = new PageableRequestDtoOfCustomerCriteriaDto({
            page: this.crtPage + 1,
            pageSize: this.pageSize
        });
        this.pageRequest.criteria = new CustomerCriteriaDto({
            searchCriteriaDto: new CustomerSearchCriteriaDto({
                customRoleKeys: null,
                siteCodes: null,
                accountEnabled: null
            }),
            sortCriteria: new SortCriteriaDto({
                direction: SortDirection.Asc,
                property: this.defaultSortProperty
            })
        });

    }

    async ngOnInit(): Promise<void> {
        await this.searchCustomers();

        this.crtLang = this.translateService.currentLang;
        this.populateMultiSelectSettings();
        this.populateRoleMultiSelects();

        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
            this.populateMultiSelectSettings();
            this.populateRoleMultiSelects();
            this.populateSiteMultiSelect();
        });

        this.customRoleService.getAllCustomRoles(false).pipe().subscribe(roles => {
            this.roles = roles;
            this.populateRoleMultiSelects();
        });

        this.siteService.getSites().subscribe(sites => {
            this.sites = sites;
            this.populateSiteMultiSelect();
        });

        this.searchTermsSubscription = this.searchTerms.valueChanges
            .pipe(
                distinctUntilChanged(),
                debounceTime(500)
            )
            .subscribe(async () => await this.searchCriteriaChanged());
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
        this.searchTermsSubscription.unsubscribe();
    }

    async setPage(page: any): Promise<void> {
        this.crtPage = page.offset;
        this.pageRequest.page = this.crtPage + 1;
        await this.searchCustomers();
    }

    async onSort(sortingValues: any): Promise<void> {
        this.goToFirstPage();
        const sortCriteriaDto: ISortCriteriaDto =
            {
                direction: sortingValues.sorts[0].dir,
                property: sortingValues.sorts[0].prop
            };
        this.pageRequest.criteria.sortCriteria = new SortCriteriaDto(sortCriteriaDto);
        await this.searchCustomers();
    }

    async searchCriteriaChanged(): Promise<void> {
        this.goToFirstPage();
        await this.searchCustomers();
    }

    private goToFirstPage(): void {
        this.crtPage = 0;
        this.pageRequest.page = this.crtPage + 1;
    }

    private async searchCustomers(): Promise<void> {
        if (this.pageRequest.criteria) {
            this.pageRequest.criteria.searchCriteriaDto.customRoleKeys = this.customRoleKeys.value?.map(item => item.itemId);
            this.pageRequest.criteria.searchCriteriaDto.accountEnabled = this.accountEnabled.value;
            this.pageRequest.criteria.searchCriteriaDto.siteCodes = this.siteCodes.value?.map(item => item.itemId);
            this.pageRequest.criteria.searchCriteriaDto.searchTerms = this.searchTerms.value;
        }

        this.customers = await this.customerService.searchCustomers(this.pageRequest).toPromise();
    }

    private populateMultiSelectSettings(): void {
        this.multiSelectSettings = {
            idField: 'itemId',
            textField: 'itemVal',
            singleSelection: false,
            selectAllText: this.translateService.instant('common.multiselect.selectAll'),
            unSelectAllText: this.translateService.instant('common.multiselect.unselectAll'),
            searchPlaceholderText: this.translateService.instant('common.multiselect.search'),
            noDataAvailablePlaceholderText: this.translateService.instant('common.multiselect.noResults'),
            itemsShowLimit: 3,
            allowSearchFilter: true
        }
    }

    private populateRoleMultiSelects(): void {
        this.customRoleMultiSelectSource = this.roles.filter(r => !r.isBase)
            .map(r => {
                return {
                    'itemId': r.roleKey,
                    'itemVal': r['label' + this.crtLang.toUpperCase()]
                }
            });
    }

    private populateSiteMultiSelect(): void {
        this.siteMultiSelectSource = this.sites
            .map(s => {
                return {
                    'itemId': s.code,
                    'itemVal': s['name' + this.crtLang.toUpperCase()]
                }
            })
            .sort((a, b) => a.itemVal.localeCompare(b.itemVal));
    }

    async clearFilter(): Promise<void> {
        this.pageRequest.page = this.crtPage + 1;
        this.pageRequest.pageSize = this.pageSize;
        this.pageRequest.criteria = new CustomerCriteriaDto({
            searchCriteriaDto: new CustomerSearchCriteriaDto({
                customRoleKeys: null,
                siteCodes: null,
                accountEnabled: null
            }),
            sortCriteria: new SortCriteriaDto({
                direction: SortDirection.Asc,
                property: this.defaultSortProperty
            })
        });

        this.searchTermsFormGroup.reset();
        this.filterFormGroup.reset();

        this.goToFirstPage();
        await this.searchCustomers();
    }

    isFiltering(): boolean {
        const searchCriteriaDto = this.pageRequest.criteria?.searchCriteriaDto;
        return (searchCriteriaDto?.customRoleKeys && searchCriteriaDto.customRoleKeys.length > 0) ||
            (searchCriteriaDto?.siteCodes && searchCriteriaDto.siteCodes.length > 0) ||
            (searchCriteriaDto?.accountEnabled != null);
    }

    openExportModal(): void {
        const modal = this.modalService.open(CustomerExportModalComponent, {centered: true, size: 'lg'});
        modal.result.then(_ => {
        }, () => {
        });
    }

    deactivateCustomer(id) {
        this.customerService.deactivateCustomer(id).pipe().subscribe(async _ => {
            this.notificationsService.success({title: 'users.notifications.deactivationSuccess'});
            await this.searchCustomers();
        });
    }

    activateCustomer(id) {
        this.customerService.activateCustomer(id).pipe().subscribe(async _ => {
            this.notificationsService.success({title: 'users.notifications.activationSuccess'});
            await this.searchCustomers();
        });
    }

    get searchTerms(): UntypedFormControl {
        return this.searchTermsFormGroup.get('searchTerms') as UntypedFormControl;
    }

    get customRoleKeys(): UntypedFormControl {
        return this.filterFormGroup.get('customRoleKeys') as UntypedFormControl;
    }

    get accountEnabled(): UntypedFormControl {
        return this.filterFormGroup.get('accountEnabled') as UntypedFormControl;
    }

    get siteCodes(): UntypedFormControl {
        return this.filterFormGroup.get('siteCodes') as UntypedFormControl;
    }
}
